import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ChevronDown, ChevronUp, FileText, CheckCircle, Lightbulb,
  PenLine, Calendar
} from "lucide-react";
import { useConsultation } from "../../context/ConsultationContext";
import ClientNoteForm from "./ClientNoteForm";
import SessionMaterialsView from "./SessionMaterialsView";

export default function ConsultationCard({ booking, isClient }) {
  const { getExpertNotes } = useConsultation();
  const [expanded, setExpanded] = useState(false);
  const [expertNotes, setExpertNotes] = useState(null);
  const [showMyNotes, setShowMyNotes] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (expanded && !loaded) {
      const load = async () => {
        const notes = await getExpertNotes(booking.id);
        setExpertNotes(notes);
        setLoaded(true);
      };
      load();
    }
  }, [expanded, loaded, booking.id, getExpertNotes]);

  const isCompleted = booking.status === "completed";

  return (
    <div
      id={`consultation-card_${booking.id}`}
      className="rounded-md bg-neutral-900 border border-neutral-800 overflow-hidden"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-5 text-left hover:bg-neutral-800/30 transition-colors"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/50 to-accent-500/50 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-medium">
              {isClient
                ? (booking.expertName?.[0] || "E")
                : (booking.clientName?.[0] || "K")}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-lg font-medium text-white truncate">
              {isClient ? booking.expertName : booking.clientName}
            </h4>
            <p className="text-lg font-extralight text-neutral-400 truncate">
              {booking.topic || "Без темы"}
            </p>
          </div>
          <div className="flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-neutral-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-neutral-500" />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 pl-[52px] text-lg font-extralight">
          <Calendar className="w-4 h-4 text-neutral-500 flex-shrink-0" />
          <span className="font-medium text-white">
            {booking.date
              ? new Date(booking.date).toLocaleDateString("ru-RU")
              : ""}
          </span>
          <span
            className={`${
              isCompleted ? "text-green-400" : "text-yellow-400"
            }`}
          >
            {isCompleted ? "Завершено" : booking.time || "Запланировано"}
          </span>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-4 border-t border-neutral-800">
              {isClient && isCompleted && (
                <div className="mt-4">
                  <SessionMaterialsView booking={booking} />
                </div>
              )}

              {isCompleted && expertNotes ? (
                <div className="mt-4 space-y-3">
                  <h5 className="text-lg font-medium text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-400" />
                    Итог от эксперта
                  </h5>
                  <div className="p-4 rounded-md bg-neutral-800/50 space-y-3">
                    <div>
                      <span className="text-lg font-extralight text-neutral-500">Краткий итог:</span>
                      <p className="text-lg font-extralight text-neutral-200 mt-1">
                        {expertNotes.summary}
                      </p>
                    </div>
                    <div>
                      <span className="text-lg font-extralight text-neutral-500 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Рекомендации:
                      </span>
                      <p className="text-lg font-extralight text-neutral-200 mt-1">
                        {expertNotes.recommendations}
                      </p>
                    </div>
                    {expertNotes.homework && (
                      <div>
                        <span className="text-lg font-extralight text-neutral-500 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" />
                          Домашнее задание:
                        </span>
                        <p className="text-lg font-extralight text-neutral-200 mt-1">
                          {expertNotes.homework}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : isCompleted && loaded ? (
                <div className="mt-4 p-4 rounded-md bg-neutral-800/30 text-center">
                  <p className="text-lg font-extralight text-neutral-500">
                    Эксперт ещё не добавил заметки по этой сессии
                  </p>
                </div>
              ) : isCompleted ? (
                <div className="mt-4 p-4 text-center">
                  <p className="text-lg font-extralight text-neutral-500">Загрузка...</p>
                </div>
              ) : null}

              {isClient && isCompleted && (
                <div className="mt-4">
                  {showMyNotes ? (
                    <ClientNoteForm
                      booking={booking}
                      onClose={() => setShowMyNotes(false)}
                      onSaved={() => setShowMyNotes(false)}
                    />
                  ) : (
                    <button
                      onClick={() => setShowMyNotes(true)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-neutral-700 hover:border-accent-500/50 hover:bg-accent-500/5 text-neutral-300 hover:text-white transition-colors"
                    >
                      <PenLine className="w-4 h-4" />
                      <span className="text-lg font-extralight">Добавить мою заметку</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
